import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { LoadingBody } from './components/loading-body';
import { WalletModalBody } from './components/modal-body';
import { WalletRegistrationErrorContextProvider } from './contexts/wallet-registration-error-context';
import { WalletRegistrationFormContextProvider } from './contexts/wallet-registration-form-context';
import { WalletRegistrationModalContextProvider } from './contexts/wallet-registration-modal-context';
import { useEligibleRequirements } from './hooks/use-eligible-requirements';
import style from './style.module.css';
import ErrorBoundary from '../../../components/error-boundary';
import ModalPortal from '../../../components/modal-portal';
import { PhoneVerificationContextProvider } from '../../../components/phone-verification/context';
import { emptyArray } from 'ui-components/default-props/empty';
import { getDisplayStringFromEnum } from '../../../enums/wallet-brand-name';
import { AddFundsContextProvider } from '../../../pages/settings/payment-and-subscription/context/add-funds-context-provider';
import useWalletAddFunds from '../../../pages/settings/payment-and-subscription/hooks/use-wallet-add-funds';
import useWalletSaveAutoFunding from '../../../pages/settings/providers/add-funds-modal/hooks/use-wallet-save-auto-funding';
import { getAccumulativeOrdersRate } from '../../../services/order-accumulation-service';
import Global from '../../../store/global-selectors';
import { refreshLabelProviders } from '../../../store/seller/seller.actions';
import { postWalletRegistrationSuccess } from '../../../store/wallet/wallet.actions';
import { WalletDataContextProvider } from '../../contexts/wallet-data-context-provider';
import { useOnWalletRegistrationOrUpdateModalClose, useOnWalletRegistrationOrUpdateModalCancel } from '../../hooks/use-on-wallet-modal';
import { useWallet } from '../../hooks/use-wallet';
import { messages } from '../../messages';
import { onWalletRegistrationSuccess } from '../../utilities/on-wallet-registration-success';

/**
 * @typedef {import('../../../financial-services-ui/types/fsui').AuctanePaySession} AuctanePaySession
 */

/**
 * @param {object} props
 * @param {string} [props.walletId]
 * @param {Array<{fulfillmentPlanId: string, shipFromId: string}>} [props.postWalletRegistrationSuccessPayload]
 * @param {() => void} [props.labelPurchaseCallback]
 * @param {string[]} [props.selectedFulfillmentPlanIds]
 * @param {string[]} [props.sellerLabelProviderIds]
 * @param {Record<string, unknown>} [props.initialFormValues]
 * @param {AuctanePaySession} [props.preloadedSession]
 * @param {() => void} [props.onClose]
 * @param {number} [props.totalCost]
 * @returns {React.ReactElement | null}
 */
export const WalletRegistrationModal = ({
  walletId: walletIdProp = '',
  postWalletRegistrationSuccessPayload = emptyArray,
  labelPurchaseCallback = noop,
  selectedFulfillmentPlanIds = emptyArray,
  sellerLabelProviderIds = emptyArray,
  initialFormValues = undefined,
  preloadedSession = undefined,
  onClose = noop,
  totalCost = undefined,
}) => {
  const { walletId } = useWallet(walletIdProp);
  // intentionally excluding postWalletRegistrationSuccessPayload for now
  // see ss-ui-client/src/wallet/modals/wallet-registration/components/modal-footer/primary-action-button/submit-button.js
  const onWalletRegOrUpdateClose = useOnWalletRegistrationOrUpdateModalClose();
  const handleClose = useCallback(() => {
    onWalletRegOrUpdateClose();
    onClose();
  }, [onWalletRegOrUpdateClose, onClose]);
  const onCancel = useOnWalletRegistrationOrUpdateModalCancel(walletId);
  const dispatch = useDispatch();

  const {
    loading,
    consolidatedRequirements,
    consolidatedTerms,
    consolidatedNonDefaultTerms,
    walletToRegister,
    initialized,
    updateWalletToRegister,
  } = useEligibleRequirements(walletId);
  const [isSubmitting, setSubmitting] = useAsyncSafeState(false);
  const [wasTermsStepSuccessful, setWasTermsStepSuccessful] = useAsyncSafeState(false);

  const onSuccess = useCallback(
    (results) => {
      onWalletRegistrationSuccess();
      updateWalletToRegister({
        ...walletToRegister,
        walletId: results.walletId,
      });
    },
    [updateWalletToRegister, walletToRegister],
  );

  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const fulfillmentPlans =
    useSelector((state) =>
      Global.selectEntitiesFulfillmentPlansByIds(state, {
        fulfillmentPlanIds: selectedFulfillmentPlanIds,
      }),
    ) || [];

  // Needed for bulk label flow total label amount
  const rates = getAccumulativeOrdersRate(fulfillmentPlans);
  const totalRateAmount = fulfillmentPlans.length > 1 ? rates : totalCost;

  const onSaveAutoFundConfig = useWalletSaveAutoFunding(walletId);
  const onAutoFundSuccess = useCallback(() => {
    dispatch(refreshLabelProviders({ shouldRefreshServices: true }));
  }, [dispatch]);

  const isFromCreateLabel = selectedFulfillmentPlanIds.length > 0;
  const showUpdateTitle = walletId && walletToRegister?.isSetupComplete;
  const onAddFunds = useWalletAddFunds(walletId);

  const setModalTitle = () => {
    if (!walletToRegister?.isSetupComplete && isFromCreateLabel) {
      return <FormattedMessage {...messages.buyFirstLabelTitle} />;
    }
    if (!walletToRegister?.isSetupComplete && !isFromCreateLabel) {
      return <FormattedMessage {...messages.activateSavingsTitle} />;
    }
    if (showUpdateTitle) {
      return (
        <FormattedMessage
          {...messages.update}
          values={{
            brand: getDisplayStringFromEnum(walletToRegister.brandName),
          }}
        />
      );
    }
    return <FormattedMessage {...messages.title} />;
  };

  const updatedOnCancel = useCallback(() => {
    // Old flow or already registered - just dispatch success and close
    if (wasTermsStepSuccessful) {
      dispatch(postWalletRegistrationSuccess());
    }
    onCancel();
  }, [wasTermsStepSuccessful, dispatch, onCancel]);

  if (!walletToRegister) {
    return null;
  }
  const ready = !loading || initialized;

  let bodyMessage = messages.exitWalletRegistrationBodyTextWithUserInput;
  if (isFromCreateLabel && wasTermsStepSuccessful) {
    bodyMessage = messages.exitLabelCreationBodyText;
  }
  if (!isFromCreateLabel && wasTermsStepSuccessful) {
    bodyMessage = messages.exitAddFundsBodyText;
  }

  return (
    <ErrorBoundary errorMessageId='walletRegistrationModal' onError={handleClose}>
      <ModalPortal
        confirmCancelBodyMessage={bodyMessage}
        confirmCancelButtonMessage={
          isFromCreateLabel && wasTermsStepSuccessful ? messages.exitLabelCreationText : messages.exitWalletRegistrationButtonText
        }
        confirmCancelHeaderMessage={
          isFromCreateLabel && wasTermsStepSuccessful ? messages.exitLabelCreationText : messages.exitWalletRegistrationText
        }
        onCancel={updatedOnCancel}
        onClose={handleClose}
      >
        <PhoneVerificationContextProvider>
          <Modal className={style['wallet-registration-modal']} size='custom'>
            <ModalHeader>{setModalTitle()}</ModalHeader>
            {!ready && <LoadingBody />}
            {ready ? (
              <WalletRegistrationFormContextProvider
                consolidatedTerms={consolidatedTerms}
                initialFormValues={initialFormValues}
                nonDefaultTerms={consolidatedNonDefaultTerms}
                postWalletRegistrationSuccessPayload={postWalletRegistrationSuccessPayload}
                registrationRequirements={consolidatedRequirements}
                walletToRegister={walletToRegister}
              >
                <WalletRegistrationModalContextProvider
                  closeWalletModal={handleClose}
                  labelPurchaseCallback={labelPurchaseCallback}
                  nonDefaultTerms={consolidatedNonDefaultTerms}
                  onSuccess={onSuccess}
                  preloadedSession={preloadedSession}
                  registrationRequirements={consolidatedRequirements}
                  selectedFulfillmentPlanIds={selectedFulfillmentPlanIds}
                  sellerLabelProviderIds={sellerLabelProviderIds}
                  terms={consolidatedTerms}
                  walletToRegister={walletToRegister}
                >
                  <WalletRegistrationErrorContextProvider>
                    <AddFundsContextProvider
                      currencyCode={sellerDefaultCurrencyCode}
                      onAddFunds={onAddFunds}
                      onSaveAutoFunding={onSaveAutoFundConfig}
                      onSuccess={onAutoFundSuccess}
                      orderRates={totalRateAmount}
                      setSubmitting={setSubmitting}
                      submitting={isSubmitting}
                    >
                      <WalletDataContextProvider>
                        <WalletModalBody setWasTermsStepSuccessful={setWasTermsStepSuccessful} />
                      </WalletDataContextProvider>
                    </AddFundsContextProvider>
                  </WalletRegistrationErrorContextProvider>
                </WalletRegistrationModalContextProvider>
              </WalletRegistrationFormContextProvider>
            ) : null}
          </Modal>
        </PhoneVerificationContextProvider>
      </ModalPortal>
    </ErrorBoundary>
  );
};
